import { ofType } from 'redux-observable';
import { mergeMap, map, filter } from 'rxjs';
import { AppEpic } from 'src/store';
import { of } from 'rxjs';
import {
  inputIsNotNullOrUndefined,
  isScopeDefined,
  isSubheaderLoaded,
  maybeGetComponentProps,
  topMemInWorklistSelected,
} from 'src/utils/Functions/epicsFunctions';
import { updateAggBys, updateAggBysNoOverwriteExisting, fetchNestedViewData } from './NestedView.slice';
import { ConfDefnComponentType, type NestedViewComponent } from 'configuration-schemas/confdefnComponents';
import { isEmpty, isNil } from 'lodash';
import {
  organizeListDataOptions,
  getConfigureModalAggBys,
  ExtraPivotOptions,
} from '../../StyleColorReview/StyleColorReview.slice';
import { isListDataApi } from 'configuration-schemas/confdefnView';
import { receiveViewDefns, updateFlowStatus } from 'src/components/Subheader/Subheader.slice';
import {
  receiveScopeConfig,
  receiveScopeRefreshTrigger,
} from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import { receiveFilterStateAfterSubmission } from 'src/components/FilterPanel/FilterPanel.slice';
import { updateSelectedItem } from 'src/pages/Worklist/Worklist.slice';
import { LENS_ASST_PLAN_REFRESHING_PAGE } from 'src/pages/AssortmentBuild/Planning.types';
import { isNotBlacklistedByDataIndex } from 'src/utils/Component/AttributeBlacklist.utils';
import { defaultsToAggBys } from 'src/utils/Component/Configure';

export const nestedViewLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      // This view's initial navigation data load is not triggered by setActivePage
      // because the updateAggBys action is triggered in the onShowView container method
      receiveScopeRefreshTrigger.type,
      receiveFilterStateAfterSubmission.type,
      updateAggBys.type,
      updateAggBysNoOverwriteExisting.type,
      updateSelectedItem.type,
      updateFlowStatus.type,
      receiveScopeConfig.type,
      LENS_ASST_PLAN_REFRESHING_PAGE,
      receiveViewDefns.type,
    ),
    map(() => maybeGetComponentProps<NestedViewComponent>(state$.value, ConfDefnComponentType.nestedView)),
    filter(inputIsNotNullOrUndefined),
    filter(() => topMemInWorklistSelected(state$.value)),
    filter(() => isScopeDefined(state$.value.scope) && isSubheaderLoaded(state$.value.subheader)),
    mergeMap((params) => {
      const { defns, topMembers } = params;
      const { dataApi, model: modelDefn } = defns;
      const finalModelDefn = isListDataApi(dataApi) ? dataApi.defnId : modelDefn;

      const flowStatus = state$.value.subheader.flowStatus.join(',');
      const aggBy = getConfigureModalAggBys(
        state$.value.pages.hindsighting.historyGrid.nestedView.aggBys,
        defns.dataApi,
      );
      const options: ExtraPivotOptions = !isNil(topMembers) ? { topMembers, flowStatus, aggBy } : { flowStatus, aggBy };
      const finalOptions = organizeListDataOptions(options, dataApi);

      // Nested View doesn't need cleanUp action on change from worklist -> non worklist
      // as the cleanUp action fires on unmount, unlike some other views (Summary, etc.)

      return of(fetchNestedViewData(finalModelDefn, finalOptions));
    }),
  );
};

/** This is here to correct aggBys state if the user scopes to a department
 * in which one of the `aggBys` is blacklisted in configure.
 * It attempts to check for all the relevant config and keys and if an `aggBys`
 * is blacklisted, then just returns defaults again
 */
export const nestedViewPruneBlacklistedAggBys: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(receiveScopeRefreshTrigger.type),
    map(() => {
      const { attributeBlacklist } = state$.value.subheader;
      const currentDepartment = state$.value.scope.scope.productMember;
      const { aggBys, viewDefns } = state$.value.pages.hindsighting.historyGrid.nestedView;
      return { attributeBlacklist, currentDepartment, aggBys, configure: viewDefns.configure };
    }),
    filter(
      ({ attributeBlacklist, currentDepartment, aggBys }) =>
        !isEmpty(attributeBlacklist) && !isEmpty(currentDepartment) && !isEmpty(aggBys),
    ),
    filter(({ attributeBlacklist, currentDepartment, aggBys }) => {
      if (!attributeBlacklist || !currentDepartment) return false;
      const keep = isNotBlacklistedByDataIndex(attributeBlacklist, currentDepartment);
      return aggBys.some((dataIndex) => !keep({ dataIndex }));
    }),
    map(({ configure }) => updateAggBys(defaultsToAggBys(configure))),
  );
};
